import { useFormik } from 'formik';

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import * as yup from 'yup';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';

import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { ToastSeverity, toastStateSelector } from 'state';
import { updatePassword } from 'api';
import { GeneralIds, RoutePaths } from 'types/types';
import RouterLink from 'components/RouterLink/RouterLink';

export default function NewPassword() {
  const { t, i18n } = useTranslation();
  const setToast = useSetRecoilState(toastStateSelector);
  const { id } = useParams<GeneralIds>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const validationSchema = yup.object({
    password: yup.string().required(t('login.errors.mandatory') ?? ''),
    confirmPassword: yup
      .string()
      .required(t('login.errors.mandatory') ?? '')
      .oneOf([yup.ref('password')], t('login.errors.passwordsMustMatch') ?? ''),
  });

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: validationSchema,
    onSubmit: async values => {
      const { password } = values;
      try {
        const response: any = await updatePassword({
          newPassword: password,
          userId: id,
          passwordResetToken: searchParams.get('token'),
        });

        const errorCode = response?.response?.data?.error?.[0].code;
        if (errorCode) {
          setToast({
            isOpen: true,
            message: i18n.exists(`errors.${errorCode}`)
              ? t(`errors.${errorCode}`)
              : t('general.errors.save'),
            severity: ToastSeverity.error,
          });
          return;
        }
        setToast({
          isOpen: true,
          message: t('login.success.passwordUpdated'),
          severity: ToastSeverity.success,
        });
        navigate(RoutePaths.login);
      } catch (error) {
        console.error(error);
        setToast({
          isOpen: true,
          message: t('login.errors.passwordResetFailed'),
          severity: ToastSeverity.error,
        });
      }
    },
  });
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{ minHeight: '100vh' }}
    >
      <Paper elevation={3}>
        <form onSubmit={formik.handleSubmit}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            p={3}
            minWidth="500px"
          >
            <TextField
              fullWidth
              margin="normal"
              id="password"
              name="password"
              type="password"
              label={t('login.password')}
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
            />
            <TextField
              fullWidth
              margin="normal"
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              label={t('login.confirmPassword')}
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              error={
                formik.touched.confirmPassword &&
                Boolean(formik.errors.confirmPassword)
              }
              helperText={
                formik.touched.confirmPassword && formik.errors.confirmPassword
              }
            />
            <Box>
              <RouterLink to="/login">{t('login.backToLogin')}</RouterLink>
            </Box>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              sx={{ marginTop: '20px' }}
            >
              {t('general.save')}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
